import { useState, useEffect, FormEvent, useMemo } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Stack } from '@/components/ui/layout';
import { Title, Text, Button, Anchor, Badge } from '@/components/ui/core';
import { PasswordInput } from '@/components/ui/inputs';
import { Alert } from '@/components/ui/feedback';
import { Progress } from '@mantine/core';
import { IconLock, IconCheck, IconAlertCircle } from '@tabler/icons-react';
import { FadeIn, AnimatedFormWrapper } from '@/components/animations';
import { useHttpClient } from '@actschurch/ccms-sdk/react';
import { validators } from '@/utils/validation';
import { LoadingScreen } from '@/components/common/LoadingScreen';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const email = searchParams.get('email');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');

  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [apiError, setApiError] = useState('');

  const httpClient = useHttpClient();
  const navigate = useNavigate();

  // Calculate password strength
  const passwordStrength = useMemo(() => {
    if (!password) return 0;
    return validators.passwordStrength(password);
  }, [password]);

  const strengthLabel = validators.passwordStrengthLabel(passwordStrength);
  const strengthColor = validators.passwordStrengthColor(passwordStrength);

  // Validate token on mount
  useEffect(() => {
    if (!token || !email) {
      setApiError('Invalid or missing reset link. Please request a new password reset.');
      setIsValidating(false);
      return;
    }

    const validateToken = async () => {
      try {
        await httpClient.get(`/users/api/auth/validate-reset-token?token=${encodeURIComponent(token)}&email=${encodeURIComponent(email)}`);
        setIsValid(true);
      } catch (err: any) {
        setApiError(err.response?.data?.message || 'Invalid or expired reset link');
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [token, email, httpClient]);

  const validate = (): boolean => {
    let isFormValid = true;

    // Validate password
    const passwordValidation = validators.password(password);
    if (passwordValidation !== true) {
      setPasswordError(passwordValidation);
      isFormValid = false;
    } else {
      setPasswordError('');
    }

    // Validate confirm password
    const confirmPasswordValidation = validators.matchPassword(confirmPassword, password);
    if (confirmPasswordValidation !== true) {
      setConfirmPasswordError(confirmPasswordValidation);
      isFormValid = false;
    } else {
      setConfirmPasswordError('');
    }

    return isFormValid;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setApiError('');

    if (!validate()) {
      return;
    }

    setIsLoading(true);

    try {
      await httpClient.post('/users/api/auth/reset-password', {
        email,
        password,
        confirmPassword,
        code: token,
      });

      setSuccess(true);

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/auth/login');
      }, 3000);
    } catch (err: any) {
      setApiError(err.response?.data?.message || err.message || 'Failed to reset password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isValidating) {
    return <LoadingScreen message="Validating reset link..." />;
  }

  if (!isValid) {
    return (
      <Stack gap="lg">
        <FadeIn delay={300}>
          <Stack align="center" gap="xs">
            <Title order={2} ta="center">
              Invalid Reset Link
            </Title>
          </Stack>
        </FadeIn>

        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Error"
          color="red"
        >
          {apiError || 'This password reset link is invalid or has expired.'}
        </Alert>

        <Button
          component={Link}
          to="/auth/forgot-password"
          variant="gradient"
          gradient={{ from: 'indigo', to: 'violet' }}
          fullWidth
        >
          Request New Reset Link
        </Button>
      </Stack>
    );
  }

  if (success) {
    return (
      <Stack gap="lg">
        <FadeIn delay={300}>
          <Stack align="center" gap="xs">
            <Title order={2} ta="center">
              Password Reset Successful
            </Title>
          </Stack>
        </FadeIn>

        <Alert
          icon={<IconCheck size={16} />}
          title="Success"
          color="green"
        >
          Your password has been reset successfully. Redirecting to login...
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      {/* Header */}
      <FadeIn delay={300}>
        <Stack align="center" gap="xs">
          <Badge
            variant="light"
            color="indigo"
            size="lg"
          >
            Password Reset
          </Badge>
          <Title order={2} ta="center" mt="md">
            Create new password
          </Title>
          <Text c="dimmed" size="sm" ta="center">
            Enter a strong password for your account
          </Text>
        </Stack>
      </FadeIn>

      {/* Error Alert */}
      {apiError && (
        <FadeIn>
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Error"
            color="red"
          >
            {apiError}
          </Alert>
        </FadeIn>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <AnimatedFormWrapper animation="stagger" delay={400} staggerDelay={100}>
          <Stack gap="xs">
            <PasswordInput
              label="New Password"
              placeholder="Create a strong password"
              leftSection={<IconLock size={16} />}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={passwordError}
              required
              size="md"
              radius="md"
            />
            {password && (
              <Stack gap={4}>
                <Progress value={passwordStrength} color={strengthColor} size="xs" />
                <Text size="xs" c={strengthColor}>
                  Strength: {strengthLabel}
                </Text>
              </Stack>
            )}
          </Stack>

          <PasswordInput
            label="Confirm New Password"
            placeholder="Confirm your password"
            leftSection={<IconLock size={16} />}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            error={confirmPasswordError}
            required
            size="md"
            radius="md"
          />

          <Button
            type="submit"
            fullWidth
            size="md"
            radius="md"
            variant="gradient"
            gradient={{ from: 'indigo', to: 'violet' }}
            loading={isLoading}
          >
            Reset Password
          </Button>
        </AnimatedFormWrapper>
      </form>

      {/* Login Link */}
      <FadeIn delay={900}>
        <Text ta="center" size="sm">
          Remember your password?{' '}
          <Anchor component={Link} to="/auth/login" fw={500}>
            Sign in
          </Anchor>
        </Text>
      </FadeIn>
    </Stack>
  );
}
